<template>
  <div>
    <b-breadcrumb size="is-large" separator="has-succeeds-separator" aria-label="breadcrumbs">
      <b-breadcrumb-item tag="a" @click.prevent="goToList()">
        {{ $t('integrations.title') }}
      </b-breadcrumb-item>
      <b-breadcrumb-item active>
        {{ $t('integrations.galleryTitle') }}
      </b-breadcrumb-item>
    </b-breadcrumb>
    <div class="box">
      <h2 class="subtitle">{{ $t('integrations.chooseType') }}</h2>
      
      <!-- Search field -->
      <div class="field mb-5">
        <div class="control has-icons-left">
          <b-input
            v-model="searchQuery"
            :placeholder="$t('integrations.searchPlaceholder')"
            type="search"
            icon="magnify"
            icon-clickable
            @icon-click="searchQuery = ''"
          />
        </div>
      </div>
      
      <div class="columns is-multiline">
        <div v-for="template in filteredTemplates" :key="template.id" class="column is-one-third">
          <div class="card is-clickable" @click="goAdd(template.id)">
            <header class="card-header">
              <p class="card-header-title">{{ template.title }}</p>
            </header>
            <div class="card-content">
              <div class="content">
                {{ template.description }}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- No results message -->
      <div v-if="filteredTemplates.length === 0 && searchQuery" class="has-text-centered py-6">
        <p class="is-size-5 has-text-grey">{{ $t('integrations.noResults') }}</p>
        <p class="has-text-grey-light">{{ $t('integrations.tryDifferentSearch') }}</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, getCurrentInstance } from 'vue'
import router from '@/router'

export default defineComponent({
  name: 'IntegrationGallery',
  setup() {
    const vm = getCurrentInstance()
    const proxy = vm?.proxy as any
    const searchQuery = ref('')

    const templates = computed(() => [
      {
        id: 'webhook',
        title: proxy.$t('integrations.customWebhook'),
        description:proxy.$t('integrations.customWebhookDesc')
      },
      {
        id: 'lambda',
        title: proxy.$t('integrations.customLambda'),
        description: proxy.$t('integrations.customLambdaDesc')
      },
      {
        id: 'anthropic-chat',
        title: 'Anthropic Chat',
        description: 'Let devices interact with Claude, Anthropic\'s advanced conversational AI.'
      },
      {
        id: 'openai-chat',
        title: 'OpenAI Chat',
        description: 'Let devices interact with GPT models like GPT-4o, GPT-4o-mini, and GPT-3.5-turbo.'
      },
      {
        id: 'losant',
        title: 'Losant Enterprise IoT Platform',
        description: 'Send device data to Losant\'s enterprise IoT platform for visualization and automation.'
      },
      {
        id: 'datadog',
        title: 'Datadog',
        description: 'Send device metrics to Datadog for monitoring, alerting, and observability.'
      },
      {
        id: 'lastfm',
        title: 'Last.fm',
        description: 'Fetch recent tracks from Last.fm to display what you\'re currently listening to.'
      },
      {
        id: 'openweather',
        title: 'OpenWeather',
        description: 'Get current weather data from OpenWeatherMap for your location.'
      },
      {
        id: 'ifttt',
        title: 'IFTTT',
        description: 'Trigger IFTTT applets and automations from your devices.'
      }
    ])

    const filteredTemplates = computed(() => {
      if (!searchQuery.value.trim()) {
        return templates.value
      }
      
      const query = searchQuery.value.toLowerCase().trim()
      return templates.value.filter(template => 
        template.title.toLowerCase().includes(query) ||
        template.description.toLowerCase().includes(query)
      )
    })

    const goToList = () => router.push({ name: 'integration-list' })
    const goAdd = (template: string) => router.push({ name: 'integration-add', params: { template }})

    return { searchQuery, filteredTemplates, goToList, goAdd }
  }
})
</script>

<style scoped>
.card {
  min-height: 170px;
}
</style>
